import { type ReactNode, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, FileText, Receipt, Landmark, Calculator,
  PieChart, BookOpen, Settings as SettingsIcon, LogOut, ChevronDown,
  Building2, ArrowLeft,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useOrg } from '../lib/org';

interface NavGroup {
  label: string;
  items: { to: string; label: string; Icon: typeof Users; }[];
}

const NAV: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    ],
  },
  {
    label: 'Sales',
    items: [
      { to: '/contacts', label: 'Contacts', Icon: Users },
      { to: '/services', label: 'Services', Icon: BookOpen },
      { to: '/quotes', label: 'Quotes', Icon: FileText },
      { to: '/invoices', label: 'Invoices', Icon: FileText },
    ],
  },
  {
    label: 'Purchases',
    items: [
      { to: '/bills', label: 'Bills', Icon: Receipt },
    ],
  },
  {
    label: 'Banking',
    items: [
      { to: '/bank', label: 'Bank accounts', Icon: Landmark },
      { to: '/bank-feeds', label: 'Bank feeds', Icon: Landmark },
    ],
  },
  {
    label: 'Payroll',
    items: [
      { to: '/employees', label: 'Employees', Icon: Users },
      { to: '/payroll', label: 'Payroll runs', Icon: Calculator },
      { to: '/emp201', label: 'EMP201', Icon: Calculator },
      { to: '/emp501', label: 'EMP501', Icon: Calculator },
    ],
  },
  {
    label: 'Tax & reports',
    items: [
      { to: '/vat201', label: 'VAT201', Icon: Calculator },
      { to: '/reports/aging', label: 'AR / AP aging', Icon: PieChart },
      { to: '/reports/pl', label: 'Profit & loss', Icon: PieChart },
      { to: '/reports/balance-sheet', label: 'Balance sheet', Icon: PieChart },
      { to: '/accounts', label: 'Chart of accounts', Icon: BookOpen },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/settings', label: 'Settings', Icon: SettingsIcon },
    ],
  },
];

export default function Shell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const { currentOrg, orgs, setCurrent } = useOrg();
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen grid" style={{ gridTemplateColumns: '260px 1fr' }}>
      {/* Sidebar */}
      <aside className="border-r border-line bg-ink-2 flex flex-col">
        <div className="px-5 pt-5 pb-3">
          <Link to="/companies" className="flex items-center gap-2.5 group" title="Back to all companies">
            <img src="/logo.svg" alt="DodoBooks" width={32} height={32} className="rounded-md" />
            <div>
              <div className="font-serif font-bold text-lg leading-none">DodoBooks</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-text-mute mt-0.5">by CloudDodo</div>
            </div>
          </Link>
          <Link to="/companies" className="mt-3 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-text-mute hover:text-emerald transition-colors">
            <ArrowLeft size={11} /> All companies
          </Link>
        </div>

        {/* Org switcher */}
        <div className="px-3 mb-2 relative">
          <button
            type="button"
            onClick={() => setOrgMenuOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-surface border border-line hover:border-line-strong text-left"
          >
            <Building2 size={16} className="text-emerald shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-text-mute font-mono">Organization</div>
              <div className="text-sm truncate">{currentOrg?.name ?? '—'}</div>
            </div>
            <ChevronDown size={16} className="text-text-mute" />
          </button>
          {orgMenuOpen && (
            <div className="absolute left-3 right-3 top-full mt-1 z-50 bg-surface-2 border border-line-strong rounded-md shadow-xl py-1 max-h-72 overflow-y-auto">
              {orgs.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => { setCurrent(o.id); setOrgMenuOpen(false); }}
                  className={
                    'w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center justify-between ' +
                    (o.id === currentOrg?.id ? 'text-emerald' : 'text-text')
                  }
                >
                  <span className="truncate">{o.name}</span>
                  {o.id === currentOrg?.id && <span className="text-xs">●</span>}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setOrgMenuOpen(false); navigate('/companies'); }}
                className="w-full text-left px-3 py-2 text-sm text-text-mute hover:bg-white/5 border-t border-line mt-1"
              >
                Manage companies →
              </button>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-4 mt-2">
          {NAV.map((group) => (
            <div key={group.label} className="mb-4">
              <div className="px-3 mb-1 text-[10px] font-mono uppercase tracking-widest text-text-mute">{group.label}</div>
              {group.items.map(({ to, label, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ' +
                    (isActive
                      ? 'bg-emerald/10 text-emerald'
                      : 'text-text-dim hover:text-text hover:bg-white/5')
                  }
                >
                  <Icon size={16} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="border-t border-line p-3">
          <div className="flex items-center gap-2 px-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-emerald/20 text-emerald grid place-items-center font-semibold text-sm">
              {(user?.email ?? '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs truncate">{user?.email}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => signOut()}
            className="btn btn-ghost btn-sm w-full justify-center"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="overflow-x-hidden">
        <div className="max-w-6xl mx-auto px-8 py-8">
          {children}
        </div>
      </main>
    </div>
  );
}
